/**
 * ApmService
 * Responsible for tracking Actions Per Minute (APM) and Clicks/Keystrokes Per Second (CPS).
 * Essential metrics for gaming and switch performance benchmarking.
 */
export class ApmService {
    constructor() {
        this.history = []; // Array of timestamps
        this.historyMaxAge = 60000; // Keep up to 60 seconds
        this.maxCps = 0;
    }

    /**
     * Register a new input event (keystroke or click).
     */
    registerAction() {
        this.history.push(performance.now());
    }

    /**
     * Calculate and return current APM, CPS, and Max CPS metrics.
     * Calculates rolling 1-second window for CPS and exponentially smoothed/extrapolated APM.
     */
    getMetrics() {
        const now = performance.now();

        // 1. Prune history strictly older than 60 seconds
        while (this.history.length > 0 && now - this.history[0] > this.historyMaxAge) {
            this.history.shift();
        }

        if (this.history.length === 0) {
            return { cps: 0, apm: 0, maxCps: this.maxCps };
        }

        // 2. CPS Calculation (Actions strictly within the last 1.0 seconds)
        const cpsWindow = 1000;
        let cpsCount = 0;
        for (let i = this.history.length - 1; i >= 0; i--) {
            if (now - this.history[i] <= cpsWindow) {
                cpsCount++;
            } else {
                break; // Array is chronologically sorted, so we can stop searching
            }
        }

        // Track Max CPS
        if (cpsCount > this.maxCps) {
            this.maxCps = cpsCount;
        }

        // 3. APM Calculation
        // Total actions divided by the elapsed time since the oldest action in the 60s window.
        // To prevent infinite/wild APM on the first instantaneous keystroke, mandate a minimum 2-second time floor.
        const firstActionTime = this.history[0];
        const elapsedTimeMs = now - firstActionTime;
        const effectiveElapsedMs = Math.max(elapsedTimeMs, 2000);
        const effectiveElapsedMinutes = effectiveElapsedMs / 60000;

        const apm = Math.round(this.history.length / effectiveElapsedMinutes);

        return { cps: cpsCount, apm, maxCps: this.maxCps };
    }

    /**
     * Reset the tracker
     */
    clear() {
        this.history = [];
        this.maxCps = 0;
    }
}

export const apmService = new ApmService();
